
import { PythonModule } from '../types';

export const PYTHON_DATABASE_BASICS_MODULE: PythonModule = {
  id: "python-database-basics",
  title: "Database Integration with SQLite",
  description: "Learn to work with databases in Python using SQLite for data storage and retrieval.",
  difficulty: "intermediate",
  content: `
# Database Integration with SQLite

Learn to integrate databases into your Python applications using SQLite for persistent data storage.

## Introduction to SQLite

SQLite is a lightweight, serverless database that's perfect for learning and small to medium applications.

### Basic Database Operations

\`\`\`python
import sqlite3
from datetime import datetime

# Connect to database (creates if doesn't exist)
conn = sqlite3.connect('example.db')
cursor = conn.cursor()

# Create table
cursor.execute('''
    CREATE TABLE IF NOT EXISTS users (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        username TEXT UNIQUE NOT NULL,
        email TEXT UNIQUE NOT NULL,
        created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
    )
''')

# Insert data
cursor.execute('''
    INSERT INTO users (username, email) 
    VALUES (?, ?)
''', ('john_doe', 'john@example.com'))

# Commit changes and close
conn.commit()
conn.close()
\`\`\`

## Database Class for Better Organization

\`\`\`python
class UserDatabase:
    def __init__(self, db_path='users.db'):
        self.db_path = db_path
        self.init_database()
    
    def get_connection(self):
        conn = sqlite3.connect(self.db_path)
        conn.row_factory = sqlite3.Row  # Enable column access by name
        return conn
    
    def init_database(self):
        with self.get_connection() as conn:
            cursor = conn.cursor()
            cursor.execute('''
                CREATE TABLE IF NOT EXISTS users (
                    id INTEGER PRIMARY KEY AUTOINCREMENT,
                    username TEXT UNIQUE NOT NULL,
                    email TEXT UNIQUE NOT NULL,
                    age INTEGER,
                    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
                )
            ''')
            conn.commit()
    
    def add_user(self, username, email, age=None):
        try:
            with self.get_connection() as conn:
                cursor = conn.cursor()
                cursor.execute('''
                    INSERT INTO users (username, email, age) 
                    VALUES (?, ?, ?)
                ''', (username, email, age))
                conn.commit()
                return cursor.lastrowid
        except sqlite3.IntegrityError as e:
            print(f"Error adding user: {e}")
            return None
    
    def get_user(self, user_id):
        with self.get_connection() as conn:
            cursor = conn.cursor()
            cursor.execute('SELECT * FROM users WHERE id = ?', (user_id,))
            return cursor.fetchone()
    
    def get_all_users(self):
        with self.get_connection() as conn:
            cursor = conn.cursor()
            cursor.execute('SELECT * FROM users ORDER BY created_at DESC')
            return cursor.fetchall()
    
    def update_user(self, user_id, username=None, email=None, age=None):
        with self.get_connection() as conn:
            cursor = conn.cursor()
            if username:
                cursor.execute('UPDATE users SET username = ? WHERE id = ?', (username, user_id))
            if email:
                cursor.execute('UPDATE users SET email = ? WHERE id = ?', (email, user_id))
            if age is not None:
                cursor.execute('UPDATE users SET age = ? WHERE id = ?', (age, user_id))
            conn.commit()
    
    def delete_user(self, user_id):
        with self.get_connection() as conn:
            cursor = conn.cursor()
            cursor.execute('DELETE FROM users WHERE id = ?', (user_id,))
            conn.commit()
            return cursor.rowcount > 0

# Usage example
db = UserDatabase()
user_id = db.add_user('alice', 'alice@example.com', 25)
if user_id:
    user = db.get_user(user_id)
    print(f"Created user: {user['username']}")
\`\`\`

## Advanced Database Operations

\`\`\`python
class TaskManager:
    def __init__(self, db_path='tasks.db'):
        self.db_path = db_path
        self.init_database()
    
    def get_connection(self):
        conn = sqlite3.connect(self.db_path)
        conn.row_factory = sqlite3.Row
        return conn
    
    def init_database(self):
        with self.get_connection() as conn:
            cursor = conn.cursor()
            
            # Create tables with foreign key relationship
            cursor.execute('''
                CREATE TABLE IF NOT EXISTS categories (
                    id INTEGER PRIMARY KEY AUTOINCREMENT,
                    name TEXT UNIQUE NOT NULL,
                    color TEXT DEFAULT '#3498db'
                )
            ''')
            
            cursor.execute('''
                CREATE TABLE IF NOT EXISTS tasks (
                    id INTEGER PRIMARY KEY AUTOINCREMENT,
                    title TEXT NOT NULL,
                    description TEXT,
                    category_id INTEGER,
                    priority INTEGER DEFAULT 1,
                    completed BOOLEAN DEFAULT FALSE,
                    due_date TEXT,
                    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
                    FOREIGN KEY (category_id) REFERENCES categories (id)
                )
            ''')
            
            conn.commit()
    
    def add_category(self, name, color='#3498db'):
        with self.get_connection() as conn:
            cursor = conn.cursor()
            cursor.execute('INSERT INTO categories (name, color) VALUES (?, ?)', (name, color))
            conn.commit()
            return cursor.lastrowid
    
    def add_task(self, title, description='', category_id=None, priority=1, due_date=None):
        with self.get_connection() as conn:
            cursor = conn.cursor()
            cursor.execute('''
                INSERT INTO tasks (title, description, category_id, priority, due_date) 
                VALUES (?, ?, ?, ?, ?)
            ''', (title, description, category_id, priority, due_date))
            conn.commit()
            return cursor.lastrowid
    
    def get_tasks_with_categories(self):
        with self.get_connection() as conn:
            cursor = conn.cursor()
            cursor.execute('''
                SELECT t.*, c.name as category_name, c.color as category_color
                FROM tasks t
                LEFT JOIN categories c ON t.category_id = c.id
                ORDER BY t.priority DESC, t.created_at DESC
            ''')
            return cursor.fetchall()
    
    def mark_completed(self, task_id):
        with self.get_connection() as conn:
            cursor = conn.cursor()
            cursor.execute('UPDATE tasks SET completed = TRUE WHERE id = ?', (task_id,))
            conn.commit()
    
    def get_task_statistics(self):
        with self.get_connection() as conn:
            cursor = conn.cursor()
            cursor.execute('''
                SELECT 
                    COUNT(*) as total_tasks,
                    SUM(CASE WHEN completed = 1 THEN 1 ELSE 0 END) as completed_tasks,
                    SUM(CASE WHEN completed = 0 THEN 1 ELSE 0 END) as pending_tasks
                FROM tasks
            ''')
            return cursor.fetchone()

# Example usage
task_manager = TaskManager()

# Add categories
work_cat = task_manager.add_category('Work', '#e74c3c')
personal_cat = task_manager.add_category('Personal', '#2ecc71')

# Add tasks
task_manager.add_task('Complete project proposal', 'Write and review the Q4 proposal', work_cat, 3)
task_manager.add_task('Buy groceries', 'Milk, bread, eggs', personal_cat, 1)

# Get tasks with category info
tasks = task_manager.get_tasks_with_categories()
for task in tasks:
    print(f"{task['title']} ({task['category_name']})")
\`\`\`

## Best Practices

1. **Use context managers** (with statements) for automatic connection cleanup
2. **Use parameterized queries** to prevent SQL injection
3. **Handle exceptions** appropriately
4. **Use transactions** for multiple related operations
5. **Create indexes** for frequently queried columns
6. **Normalize your database design** to reduce redundancy

## Practice Exercise

Build a personal expense tracker that stores transactions with categories, dates, and amounts, and provides spending analysis features.
`,
  duration: "70 minutes",
  objectives: [
    "Understand database concepts and SQLite basics",
    "Perform CRUD operations with Python and SQLite",
    "Design normalized database schemas",
    "Implement database classes for code organization",
    "Apply best practices for database security and performance"
  ],
  order: 7
};
